/**
 * Typed Socket.io client. Connects to the same origin in production (Express
 * serves both the API and the built web app) and to the Vite proxy in dev.
 *
 * Every connection carries the signed-in user's Firebase ID token so the
 * server can authenticate it — the server rejects unauthenticated sockets to
 * keep the kitchen order feed (which includes teacher PII) private. The token
 * is supplied via a function so it's refreshed on every reconnect attempt.
 */
import type { ClientToServerEvents, ServerToClientEvents } from '@cracked-mug/shared';
import { io, type Socket } from 'socket.io-client';
import { auth } from '../firebase';

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: AppSocket | null = null;

export function getSocket(): AppSocket {
  // Prefer WebSocket, but keep long-polling as a fallback so the kitchen board
  // and order tracking still receive live updates on locked-down school
  // networks that block raw WebSocket upgrades. The server is a single Render
  // instance, so polling has no sticky-session concern.
  socket ??= io({
    autoConnect: true,
    transports: ['websocket', 'polling'],
    // Resolved before each (re)connect, so reconnects always carry a fresh,
    // non-expired token. If no one is signed in we send nothing and the server
    // rejects the connection (the app falls back to its REST + refetch paths).
    auth: (cb: (data: { token?: string }) => void) => {
      const user = auth.currentUser;
      if (!user) {
        cb({});
        return;
      }
      user
        .getIdToken()
        .then((token) => cb({ token }))
        .catch(() => cb({}));
    },
  });
  return socket;
}

/**
 * Tear down the current socket so the next `getSocket()` opens a fresh,
 * re-authenticated connection. Called on every auth-state change so a previous
 * signed-in user on a shared device can't leave an authenticated socket (e.g.
 * one still joined to the kitchen room) open for whoever signs in next.
 */
export function resetSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
